import React, { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Row, Col, CardBody, Card } from "reactstrap";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../Loaders/DnaLoader";
import axios from "axios";
import Swal from "sweetalert2";
import { getDisplayFileName, uploadFile, getMerchantLogoUrl } from "../../utils/fileUtils";
const API_BASE_URL = process.env.REACT_APP_APIURL;
const FILE_BASE_URL = process.env.REACT_APP_FILE_URL || "http://localhost:3012";

const MerchantForm = ({ isEdit = false }) => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [banks, setBanks] = useState([]);
  const [completedTabs, setCompletedTabs] = useState(new Set());

  // Tab navigation order
  const tabOrder = ["basic", "address", "documents", "financial", "settings"];

  // Form state
  const [merchantData, setMerchantData] = useState({
    // Basic Info
    merchantName: "",
    merchantType: "Pharmacy",
    companyName: "",
    registrationNumber: "",
    businessCategory: "Pharmacy",

    // Contact Info
    contactPerson: "",
    contactEmail: "",
    contactPhone: "",
    alternativePhone: "",

    // Address
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postcode: "",
      country: "",
      googleMapLocation: "",
    },

    // Account
    loginEmail: "",
    status: "Under Approval",

    // Financial
    bankInfo: {
      bankName: "",
      accountNumber: "",
      accountHolderName: "",
      paymentTerms: "Monthly",
    },

    // Settings
    commissionRate: "",
    storeDescription: "",
    openingHours: {
      monday: { open: "09:00", close: "18:00" },
      tuesday: { open: "09:00", close: "18:00" },
      wednesday: { open: "09:00", close: "18:00" },
      thursday: { open: "09:00", close: "18:00" },
      friday: { open: "09:00", close: "18:00" },
      saturday: { open: "09:00", close: "18:00" },
      sunday: { open: "09:00", close: "18:00" },
    },
  });

  // File uploads
  const [files, setFiles] = useState({});
  const [fileInfo, setFileInfo] = useState({});

  // Add state for merchant docs
  const [merchantDocs, setMerchantDocs] = useState([]);

  // Fetch merchant data if in edit mode
  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('language'));
    if (isEdit && id) {
      fetchMerchantData();
    }
  }, [isEdit, id]);

  // Fetch merchant docs on component mount
  useEffect(() => {
    fetchMerchantDocs();
  }, []);

  const fetchMerchantData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/MerchantModule/${id}`);

      if (response.data.success) {
        const merchant = response.data.data;

        // Update form state with merchant data
        setMerchantData({
          merchantName: merchant.merchantName || "",
          merchantType: merchant.merchantType || "Pharmacy",
          companyName: merchant.companyName || "",
          registrationNumber: merchant.registrationNumber || "",
          businessCategory: merchant.businessCategory || "Pharmacy",
          contactPerson: merchant.contactPerson || "",
          contactEmail: merchant.contactEmail || "",
          contactPhone: merchant.contactPhone || "",
          alternativePhone: merchant.alternativePhone || "",
          address: merchant.address || {
            line1: "",
            line2: "",
            city: "",
            state: "",
            postcode: "",
            country: "",
            googleMapLocation: "",
          },
          loginEmail: merchant.loginEmail || "",
          status: merchant.status || "Under Approval",
          bankInfo: merchant.bankInfo || {
            bankName: "",
            accountNumber: "",
            accountHolderName: "",
            paymentTerms: "Monthly",
          },
          commissionRate: merchant.hasCustomCommissionRate ? (merchant.commissionRate ?? "") : "",
          storeDescription: merchant.storeDescription || "",
          openingHours: merchant.openingHours || {
            monday: { open: "09:00", close: "18:00" },
            tuesday: { open: "09:00", close: "18:00" },
            wednesday: { open: "09:00", close: "18:00" },
            thursday: { open: "09:00", close: "18:00" },
            friday: { open: "09:00", close: "18:00" },
            saturday: { open: "09:00", close: "18:00" },
            sunday: { open: "09:00", close: "18:00" },
          },
        });

        // Update file info for display
        if (merchant.documents) {
          setFileInfo(merchant.documents);
        }
        if (merchant.logo) {
          setFileInfo((prev) => ({ ...prev, logo: merchant.logo }));
        }

        // Mark all tabs as completed for edit mode
        setCompletedTabs(new Set(tabOrder));
      }
    } catch (error) {
      console.error("Error fetching merchant data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to load merchant data",
        text:
          error.response?.data?.message ||
          "An error occurred while fetching merchant data",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const requestData = {
        functionName: 'GetBanks',
        postData: {
          offset: 0,
          limit: 100, // Get all banks for dropdown
          sort: { datetime_added: -1 },
          noActiveMatch: true // Get all banks including inactive ones
        }
      };

      const headers = {
        'Content-Type': 'application/json',
        'token': localStorage.getItem('session-id')
      };

      const response = await axios.post(`${API_BASE_URL}/GetBanks`, requestData, { headers });
      
      if (response.data.status && response.data.data) {
        setBanks(response.data.data || []);
      } else {
        console.error("Error fetching banks:", response.data.message);
        setBanks([]);
      }
    } catch (err) {
      console.error("Error fetching banks:", err);
      setBanks([]); // Set empty array as fallback
    }
  };

  const fetchMerchantDocs = async () => {
    try {
      const requestData = {
        functionName: 'GetMerchantDocs',
        postData: {
          offset: 0,
          limit: 100, // Get all merchant docs for dropdown
          sort: { datetime_added: -1 },
          noActiveMatch: true // Get all docs including inactive ones
        }
      };

      const headers = {
        'Content-Type': 'application/json',
        'token': localStorage.getItem('session-id')
      };

      const response = await axios.post(`${API_BASE_URL}/GetMerchantDocs`, requestData, { headers });
      
      if (response.data.status && response.data.data) {
        setMerchantDocs(response.data.data || []);
      } else {
        console.error("Error fetching merchant docs:", response.data.message);
        setMerchantDocs([]);
      }
    } catch (error) {
      console.error("Error fetching merchant docs:", error);
      setMerchantDocs([]); // Set empty array as fallback
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const validateField = (name, value) => {
    let error = "";

    if (name === "merchantName" && !value.trim()) {
      error = "Merchant name is required";
    } else if (name === "companyName" && !value.trim()) {
      error = "Company name is required";
    } else if (name === "registrationNumber" && !value.trim()) {
      error = "Registration number is required";
    } else if (name === "contactPerson" && !value.trim()) {
      error = "Contact person name is required";
    } else if (name === "contactEmail") {
      if (!value.trim()) {
        error = "Contact email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error = "Please enter a valid email address";
      }
    } else if (name === "contactPhone") {
      if (!value.trim()) {
        error = "Contact phone is required";
      } else if (
        !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(value)
      ) {
        error = "Please enter a valid phone number";
      }
    } else if (name === "loginEmail") {
      if (!value.trim()) {
        error = "Login email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        error = "Please enter a valid email address";
      }
    } else if (name === "address.line1" && !value.trim()) {
      error = "Address line 1 is required";
    } else if (name === "address.city" && !value.trim()) {
      error = "City is required";
    } else if (name === "address.state" && !value.trim()) {
      error = "State is required";
    } else if (name === "address.postcode" && !value.trim()) {
      error = "Postal code is required";
    } else if (name === "address.country" && !value.trim()) {
      error = "Country is required";
    } else if (name === "commissionRate") {
      if (value !== "" && (isNaN(value) || Number(value) < 0 || Number(value) > 100)) {
        error = "Share must be between 0 and 100";
      }
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Validate the field
    const error = validateField(name, value);

    // Update errors state
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    // Handle nested objects
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setMerchantData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setMerchantData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Only allow form submission in settings tab, block Enter navigation elsewhere
      if (activeTab === 'settings') {
        handleSubmit(e);
      }
      // Remove all Enter key navigation - users must click Next button
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setMerchantData((prev) => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value,
        },
      },
    }));
  };

  // Navigation functions
  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };

  const goToPreviousTab = () => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1]);
    }
  };

  const isLastTab = () => {
    return tabOrder.indexOf(activeTab) === tabOrder.length - 1;
  };

  const isFirstTab = () => {
    return tabOrder.indexOf(activeTab) === 0;
  };

  // Check if a tab can be accessed (all previous tabs must be completed)
  const canAccessTab = (tabName) => {
    const tabIndex = tabOrder.indexOf(tabName);
    if (tabIndex === 0) return true; // First tab is always accessible
    
    // Check if all previous tabs are completed
    for (let i = 0; i < tabIndex; i++) {
      if (!completedTabs.has(tabOrder[i])) {
        return false;
      }
    }
    return true;
  };

  // Handle tab click with validation
  const handleTabClick = (tabName) => {
    if (canAccessTab(tabName)) {
      setActiveTab(tabName);
      // Mark documents tab as completed when accessed (since it's optional)
      if (tabName === "documents") {
        setCompletedTabs(prev => new Set([...prev, "documents"]));
      }
    } else {
      // Find the first incomplete tab
      const firstIncompleteTab = tabOrder.find(tab => !completedTabs.has(tab));
      if (firstIncompleteTab) {
        setActiveTab(firstIncompleteTab);
        Swal.fire({
          icon: "warning",
          title: "Complete Previous Steps",
          text: `Please complete the ${firstIncompleteTab} section before accessing other tabs.`,
          confirmButtonColor: "#00BFA6",
        });
      }
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [fileType]: "File size should not exceed 5MB",
        }));
        return;
      }

      setFiles((prev) => ({
        ...prev,
        [fileType]: file,
      }));

      // Clear any previous errors
      setErrors((prev) => ({
        ...prev,
        [fileType]: "",
      }));
    }
  };

  const validateCurrentTab = () => {
    const newErrors = {};

    if (activeTab === "basic") {
      // Validate basic info
      newErrors.merchantName = validateField(
        "merchantName",
        merchantData.merchantName
      );
      newErrors.companyName = validateField(
        "companyName",
        merchantData.companyName
      );
      newErrors.registrationNumber = validateField(
        "registrationNumber",
        merchantData.registrationNumber
      );
      newErrors.contactPerson = validateField(
        "contactPerson",
        merchantData.contactPerson
      );
      newErrors.contactEmail = validateField(
        "contactEmail",
        merchantData.contactEmail
      );
      newErrors.contactPhone = validateField(
        "contactPhone",
        merchantData.contactPhone
      );
      newErrors.loginEmail = validateField("loginEmail", merchantData.loginEmail);
    } else if (activeTab === "address") {
      // Validate address
      newErrors["address.line1"] = validateField(
        "address.line1",
        merchantData.address.line1
      );
      newErrors["address.city"] = validateField(
        "address.city",
        merchantData.address.city
      );
      newErrors["address.state"] = validateField(
        "address.state",
        merchantData.address.state
      );
      newErrors["address.postcode"] = validateField(
        "address.postcode",
        merchantData.address.postcode
      );
      newErrors["address.country"] = validateField(
        "address.country",
        merchantData.address.country
      );
    } else if (activeTab === "financial") {
      // Validate financial info
      newErrors.commissionRate = validateField(
        "commissionRate",
        merchantData.commissionRate
      );
    } else if (activeTab === "settings") {
      // Validate settings - commission rate is required
      newErrors.commissionRate = validateField(
        "commissionRate",
        merchantData.commissionRate
      );
    }

    setErrors(newErrors);

    // Check if there are any errors
    const isValid = !Object.values(newErrors).some((error) => error !== "");
    
    // Mark tab as completed if valid
    if (isValid) {
      setCompletedTabs(prev => new Set([...prev, activeTab]));
    }
    
    return isValid;
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate basic info
    newErrors.merchantName = validateField(
      "merchantName",
      merchantData.merchantName
    );
    newErrors.companyName = validateField(
      "companyName",
      merchantData.companyName
    );
    newErrors.registrationNumber = validateField(
      "registrationNumber",
      merchantData.registrationNumber
    );
    newErrors.contactPerson = validateField(
      "contactPerson",
      merchantData.contactPerson
    );
    newErrors.contactEmail = validateField(
      "contactEmail",
      merchantData.contactEmail
    );
    newErrors.contactPhone = validateField(
      "contactPhone",
      merchantData.contactPhone
    );
    newErrors.loginEmail = validateField("loginEmail", merchantData.loginEmail);

    // Validate address
    newErrors["address.line1"] = validateField(
      "address.line1",
      merchantData.address.line1
    );
    newErrors["address.city"] = validateField(
      "address.city",
      merchantData.address.city
    );
    newErrors["address.state"] = validateField(
      "address.state",
      merchantData.address.state
    );
    newErrors["address.postcode"] = validateField(
      "address.postcode",
      merchantData.address.postcode
    );
    newErrors["address.country"] = validateField(
      "address.country",
      merchantData.address.country
    );

    // Validate financial info
    newErrors.commissionRate = validateField(
      "commissionRate",
      merchantData.commissionRate
    );

    setErrors(newErrors);

    // Check if there are any errors
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleNext = () => {
    console.log("handleNext called, activeTab:", activeTab);
    // Validate current tab before proceeding
    const isValid = validateCurrentTab();
    if (!isValid) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please correct the errors in the current tab before proceeding.",
      });
      return;
    }
    console.log("Moving to next tab");
    goToNextTab();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("handleSubmit called, activeTab:", activeTab);

    // Only allow submission from the last tab (settings)
    if (activeTab !== "settings") {
      console.log("Form submission blocked - not in settings tab");
      return;
    }

    // Validate all fields before submission
    const isValid = validateForm();
    if (!isValid) {
      // Find the first tab with errors and switch to it
      if (
        errors.merchantName ||
        errors.companyName ||
        errors.registrationNumber ||
        errors.contactPerson ||
        errors.contactEmail ||
        errors.contactPhone ||
        errors.loginEmail
      ) {
        setActiveTab("basic");
      } else if (
        errors["address.line1"] ||
        errors["address.city"] ||
        errors["address.state"] ||
        errors["address.postcode"] ||
        errors["address.country"]
      ) {
        setActiveTab("address");
      } else if (errors.commissionRate) {
        setActiveTab("financial");
      }

      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please correct the errors in the form before submitting.",
      });
      return;
    }

    try {
      setSubmitting(true);

      let merchantId;
      let tempPassword = null;

      // Step 1: Create or update basic merchant info
      if (isEdit) {
        // Update existing merchant
        const basicResponse = await axios.put(
          `${API_BASE_URL}/MerchantModule/update-basic/${id}`,
          {
            merchantName: merchantData.merchantName,
            merchantType: merchantData.merchantType,
            companyName: merchantData.companyName,
            registrationNumber: merchantData.registrationNumber,
            businessCategory: merchantData.businessCategory,
            contactPerson: merchantData.contactPerson,
            contactEmail: merchantData.contactEmail,
            contactPhone: merchantData.contactPhone,
            alternativePhone: merchantData.alternativePhone,
            loginEmail: merchantData.loginEmail,
          }
        );

        if (!basicResponse.data.success) {
          throw new Error(
            basicResponse.data.message || "Failed to update merchant basic info"
          );
        }

        merchantId = id;
      } else {
        // Create new merchant
        const createResponse = await axios.post(
          `${API_BASE_URL}/MerchantModule/create`,
          {
            merchantName: merchantData.merchantName,
            merchantType: merchantData.merchantType,
            companyName: merchantData.companyName,
            registrationNumber: merchantData.registrationNumber,
            businessCategory: merchantData.businessCategory,
            contactPerson: merchantData.contactPerson,
            contactEmail: merchantData.contactEmail,
            contactPhone: merchantData.contactPhone,
            alternativePhone: merchantData.alternativePhone,
            loginEmail: merchantData.loginEmail,
          }
        );

        if (!createResponse.data.success) {
          throw new Error(
            createResponse.data.message || "Failed to create merchant"
          );
        }

        merchantId = createResponse.data.data._id;
        tempPassword = createResponse.data.tempPassword;
      }

      // Step 2: Update address
      await axios.put(
        `${API_BASE_URL}/MerchantModule/update-address/${merchantId}`,
        {
          line1: merchantData.address.line1,
          line2: merchantData.address.line2,
          city: merchantData.address.city,
          state: merchantData.address.state,
          postcode: merchantData.address.postcode,
          country: merchantData.address.country,
          googleMapLocation: merchantData.address.googleMapLocation,
        }
      );

      // Step 3: Update bank info
      await axios.put(
        `${API_BASE_URL}/MerchantModule/update-bank/${merchantId}`,
        {
          bankName: merchantData.bankInfo.bankName,
          accountNumber: merchantData.bankInfo.accountNumber,
          accountHolderName: merchantData.bankInfo.accountHolderName,
          paymentTerms: merchantData.bankInfo.paymentTerms,
        }
      );

      // Step 4: Update settings
      await axios.put(
        `${API_BASE_URL}/MerchantModule/update-settings/${merchantId}`,
        {
          commissionRate: merchantData.commissionRate === '' ? null : merchantData.commissionRate,
          storeDescription: merchantData.storeDescription,
          openingHours: merchantData.openingHours,
        }
      );

      // Step 5: Update status
      await axios.put(
        `${API_BASE_URL}/MerchantModule/update-status/${merchantId}`,
        {
          status: merchantData.status,
        }
      );

      // Step 6: Upload files if any
      for (const [fileType, file] of Object.entries(files)) {
        if (!file) continue;

        if (fileType === "logo") {
          const uploadedPath = await uploadFile(file, "image");
          await axios.put(
            `${API_BASE_URL}/MerchantModule/update-settings/${merchantId}`,
            {
              logo: uploadedPath,
            }
          );
          continue;
        }

        const formData = new FormData();
        formData.append("document", file);

        if (fileType === "otherDocs") {
          formData.append("description", "Additional document");
          await axios.post(
            `${API_BASE_URL}/MerchantModule/upload-document/${merchantId}/other`,
            formData
          );
        } else {
          await axios.post(
            `${API_BASE_URL}/MerchantModule/upload-document/${merchantId}/${fileType}`,
            formData
          );
        }
      }

      // Show success message and password for new merchants
      if (!isEdit && tempPassword) {
        await Swal.fire({
          icon: "success",
          title: "Merchant Created Successfully!",
          html: `
            <div class="text-start">
              <p><strong>Merchant Name:</strong> ${merchantData.merchantName}</p>
              <p><strong>Login Email:</strong> ${merchantData.loginEmail}</p>
              <p><strong>Temporary Password:</strong> <code class="bg-light p-2 rounded">${tempPassword}</code></p>
              <p class="text-warning mt-3"><i class="pe-7s-info me-1"></i> Please save this password. It will not be shown again.</p>
            </div>
          `,
          confirmButtonText: "Save & Continue",
          confirmButtonColor: "#00BFA6",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
      } else {
        Swal.fire({
          icon: "success",
          title: isEdit ? "Merchant Updated" : "Merchant Created",
          text: isEdit
            ? "Merchant information has been updated successfully"
            : "New merchant has been created successfully",
          confirmButtonColor: "#00BFA6",
        });
      }

      // Redirect to merchant list
      navigate("/merchants");
    } catch (error) {
      console.error("Error submitting merchant data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to save merchant",
        text: error.message || "An error occurred while saving merchant data",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendCredentials = async () => {
    try {
      setSubmitting(true);

      const response = await axios.post(
        `${API_BASE_URL}/MerchantModule/send-credentials/${id}`
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Credentials Sent",
          text: "Login credentials have been sent to the merchant email",
          confirmButtonColor: "#00BFA6",
        });
      } else {
        throw new Error(response.data.message || "Failed to send credentials");
      }
    } catch (error) {
      console.error("Error sending credentials:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to Send Credentials",
        text:
          error.message || "An error occurred while sending login credentials",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <div className="bg-light-gray bg-animation">
            <div className="d-flex justify-content-center align-items-center">
              <Col md="12" className="mx-auto app-login-box">
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-user icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {isEdit ? t("Edit Merchant") : t("Create New Merchant")}
                        <div className="page-title-subheading">
                          {isEdit
                            ? t("Update merchant details and information")
                            : t("Enter merchant details to create a new account")}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <button
                        type="button"
                        onClick={() => navigate("/merchants")}
                        className="btn btn-custom-color btn-hover-shine me-2"
                      >
                        <i className="pe-7s-back me-1"></i>
                        {t("Back")}
                      </button>
                    </div>
                  </div>
                </div>

                <Row>
                  <LoadingOverlay
                    tag="div"
                    active={loading}
                    styles={{ overlay: (base) => ({ ...base }) }}
                    spinner={<DNALoader />}
                  >
                    <Col md="12">
                      <Card className="main-card mb-3">
                        <CardBody>
                          <div className="mb-4">
                            <div className="nav nav-pills nav-fill" role="tablist">
                              <button
                                className={`nav-link rounded-pill me-2 position-relative ${
                                  activeTab === "basic"
                                    ? "active text-white shadow-lg"
                                    : canAccessTab("basic") 
                                      ? "text-dark bg-light border-0"
                                      : "text-muted bg-light border-0 opacity-50"
                                }`}
                                onClick={() => handleTabClick("basic")}
                                disabled={!canAccessTab("basic")}
                                title={!canAccessTab("basic") ? "Complete previous steps first" : ""}
                                style={{
                                  transition: 'all 0.3s ease',
                                  transform: activeTab === "basic" ? 'scale(1.05)' : 'scale(1)',
                                  backgroundColor: activeTab === "basic" ? 'var(--buttons-color, #3ac47d)' : '',
                                  boxShadow: activeTab === "basic" ? '0 8px 25px rgba(58,196,125,0.3)' : '0 2px 10px rgba(0,0,0,0.1)',
                                  cursor: canAccessTab("basic") ? 'pointer' : 'not-allowed'
                                }}
                              >
                                <i className={`pe-7s-user me-2 ${completedTabs.has("basic") ? "text-success" : ""}`}></i>
                                {t("Basic Info")}
                                {completedTabs.has("basic") && (
                                  <i className="pe-7s-check text-success ms-1"></i>
                                )}
                              </button>
                              <button
                                className={`nav-link rounded-pill me-2 position-relative ${
                                  activeTab === "address"
                                    ? "active text-white shadow-lg"
                                    : canAccessTab("address") 
                                      ? "text-dark bg-light border-0"
                                      : "text-muted bg-light border-0 opacity-50"
                                }`}
                                onClick={() => handleTabClick("address")}
                                disabled={!canAccessTab("address")}
                                title={!canAccessTab("address") ? "Complete previous steps first" : ""}
                                style={{
                                  transition: 'all 0.3s ease',
                                  transform: activeTab === "address" ? 'scale(1.05)' : 'scale(1)',
                                  backgroundColor: activeTab === "address" ? 'var(--buttons-color, #3ac47d)' : '',
                                  boxShadow: activeTab === "address" ? '0 8px 25px rgba(58,196,125,0.3)' : '0 2px 10px rgba(0,0,0,0.1)',
                                  cursor: canAccessTab("address") ? 'pointer' : 'not-allowed'
                                }}
                              >
                                <i className={`pe-7s-map-marker me-2 ${completedTabs.has("address") ? "text-success" : ""}`}></i>
                                {t("Address")}
                                {completedTabs.has("address") && (
                                  <i className="pe-7s-check text-success ms-1"></i>
                                )}
                              </button>
                              <button
                                className={`nav-link rounded-pill me-2 position-relative ${
                                  activeTab === "documents"
                                    ? "active text-white shadow-lg"
                                    : canAccessTab("documents") 
                                      ? "text-dark bg-light border-0"
                                      : "text-muted bg-light border-0 opacity-50"
                                }`}
                                onClick={() => handleTabClick("documents")}
                                disabled={!canAccessTab("documents")}
                                title={!canAccessTab("documents") ? "Complete previous steps first" : ""}
                                style={{
                                  transition: 'all 0.3s ease',
                                  transform: activeTab === "documents" ? 'scale(1.05)' : 'scale(1)',
                                  backgroundColor: activeTab === "documents" ? 'var(--buttons-color, #3ac47d)' : '',
                                  boxShadow: activeTab === "documents" ? '0 8px 25px rgba(58,196,125,0.3)' : '0 2px 10px rgba(0,0,0,0.1)',
                                  cursor: canAccessTab("documents") ? 'pointer' : 'not-allowed'
                                }}
                              >
                                <i className={`pe-7s-file me-2 ${completedTabs.has("documents") ? "text-success" : ""}`}></i>
                                {t("Documents")}
                                {completedTabs.has("documents") && (
                                  <i className="pe-7s-check text-success ms-1"></i>
                                )}
                              </button>
                              <button
                                className={`nav-link rounded-pill me-2 position-relative ${
                                  activeTab === "financial"
                                    ? "active text-white shadow-lg"
                                    : canAccessTab("financial") 
                                      ? "text-dark bg-light border-0"
                                      : "text-muted bg-light border-0 opacity-50"
                                }`}
                                onClick={() => handleTabClick("financial")}
                                disabled={!canAccessTab("financial")}
                                title={!canAccessTab("financial") ? "Complete previous steps first" : ""}
                                style={{
                                  transition: 'all 0.3s ease',
                                  transform: activeTab === "financial" ? 'scale(1.05)' : 'scale(1)',
                                  backgroundColor: activeTab === "financial" ? 'var(--buttons-color, #3ac47d)' : '',
                                  boxShadow: activeTab === "financial" ? '0 8px 25px rgba(58,196,125,0.3)' : '0 2px 10px rgba(0,0,0,0.1)',
                                  cursor: canAccessTab("financial") ? 'pointer' : 'not-allowed'
                                }}
                              >
                                <i className={`pe-7s-cash me-2 ${completedTabs.has("financial") ? "text-success" : ""}`}></i>
                                {t("Financial")}
                                {completedTabs.has("financial") && (
                                  <i className="pe-7s-check text-success ms-1"></i>
                                )}
                              </button>
                              <button
                                className={`nav-link rounded-pill position-relative ${
                                  activeTab === "settings"
                                    ? "active text-white shadow-lg"
                                    : canAccessTab("settings") 
                                      ? "text-dark bg-light border-0"
                                      : "text-muted bg-light border-0 opacity-50"
                                }`}
                                onClick={() => handleTabClick("settings")}
                                disabled={!canAccessTab("settings")}
                                title={!canAccessTab("settings") ? "Complete previous steps first" : ""}
                                style={{
                                  transition: 'all 0.3s ease',
                                  transform: activeTab === "settings" ? 'scale(1.05)' : 'scale(1)',
                                  backgroundColor: activeTab === "settings" ? 'var(--buttons-color, #3ac47d)' : '',
                                  boxShadow: activeTab === "settings" ? '0 8px 25px rgba(58,196,125,0.3)' : '0 2px 10px rgba(0,0,0,0.1)',
                                  cursor: canAccessTab("settings") ? 'pointer' : 'not-allowed'
                                }}
                              >
                                <i className={`pe-7s-config me-2 ${completedTabs.has("settings") ? "text-success" : ""}`}></i>
                                {t("Settings")}
                                {completedTabs.has("settings") && (
                                  <i className="pe-7s-check text-success ms-1"></i>
                                )}
                              </button>
                            </div>
                          </div>

                          <form onSubmit={(e) => {
                            console.log("Form onSubmit triggered, activeTab:", activeTab);
                            e.preventDefault();
                            if (activeTab === 'settings') {
                              console.log("Calling handleSubmit from form onSubmit");
                              handleSubmit(e);
                            } else {
                              console.log("Form submission blocked - not in settings tab");
                            }
                          }} onKeyPress={handleKeyPress} noValidate>
                            {/* Basic Info Tab */}
                            {activeTab === "basic" && (
                              <Row className="g-4">
                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Merchant Name")} ({t("Store Name")}) *
                                    </label>
                                    <input
                                      type="text"
                                      name="merchantName"
                                      value={merchantData.merchantName}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.merchantName ? "is-invalid" : ""
                                      }`}
                                      placeholder={t("Enter merchant name")}
                                    />
                                    {errors.merchantName && (
                                      <div className="invalid-feedback">
                                        {errors.merchantName}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Merchant Type")} *
                                    </label>
                                    <select
                                      name="merchantType"
                                      value={merchantData.merchantType}
                                      onChange={handleInputChange}
                                      required
                                      className="form-select"
                                    >
                                      <option value="Pharmacy">{t("Pharmacy")}</option>
                                      <option value="IoT & Lifestyle">{t("IoT & Lifestyle")}</option>
                                    </select>
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Company Name")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="companyName"
                                      value={merchantData.companyName}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.companyName ? "is-invalid" : ""
                                      }`}
                                      placeholder={t("Enter company name")}
                                    />
                                    {errors.companyName && (
                                      <div className="invalid-feedback">
                                        {errors.companyName}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Business Registration Number")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="registrationNumber"
                                      value={merchantData.registrationNumber}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.registrationNumber ? "is-invalid" : ""
                                      }`}
                                      placeholder={t("Enter registration number")}
                                    />
                                    {errors.registrationNumber && (
                                      <div className="invalid-feedback">
                                        {errors.registrationNumber}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Business Category")} *
                                    </label>
                                    <select
                                      name="businessCategory"
                                      value={merchantData.businessCategory}
                                      onChange={handleInputChange}
                                      required
                                      className="form-select"
                                    >
                                      <option value="Pharmacy">{t("Pharmacy")}</option>
                                      <option value="eCommerce">{t("eCommerce")}</option>
                                    </select>
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Contact Person Name")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="contactPerson"
                                      value={merchantData.contactPerson}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.contactPerson ? "is-invalid" : ""
                                      }`}
                                      placeholder={t("Enter contact person name")}
                                    />
                                    {errors.contactPerson && (
                                      <div className="invalid-feedback">
                                        {errors.contactPerson}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Contact Email")} *
                                    </label>
                                    <input
                                      type="email"
                                      name="contactEmail"
                                      value={merchantData.contactEmail}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.contactEmail ? "is-invalid" : ""
                                      }`}
                                      placeholder="example@domain.com"
                                    />
                                    {errors.contactEmail && (
                                      <div className="invalid-feedback">
                                        {errors.contactEmail}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Contact Phone")} *
                                    </label>
                                    <input
                                      type="tel"
                                      name="contactPhone"
                                      value={merchantData.contactPhone}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.contactPhone ? "is-invalid" : ""
                                      }`}
                                      placeholder="+1 (123) 456-7890"
                                    />
                                    {errors.contactPhone && (
                                      <div className="invalid-feedback">
                                        {errors.contactPhone}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Alternative Phone")}
                                    </label>
                                    <input
                                      type="tel"
                                      name="alternativePhone"
                                      value={merchantData.alternativePhone}
                                      onChange={handleInputChange}
                                      className="form-control"
                                      placeholder="+1 (123) 456-7890"
                                    />
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Login Email")} *
                                    </label>
                                    <input
                                      type="email"
                                      name="loginEmail"
                                      value={merchantData.loginEmail}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors.loginEmail ? "is-invalid" : ""
                                      }`}
                                      placeholder="login@domain.com"
                                    />
                                    {errors.loginEmail && (
                                      <div className="invalid-feedback">
                                        {errors.loginEmail}
                                      </div>
                                    )}
                                    <small className="form-text text-muted">
                                      {t("This email will be used for login and account correspondence.")}
                                    </small>
                                  </div>
                                </Col>

                                {isEdit && (
                                  <Col md="6">
                                    <div className="form-group">
                                      <label className="form-label">
                                        {t("Account Status")}
                                      </label>
                                      <select
                                        name="status"
                                        value={merchantData.status}
                                        onChange={handleInputChange}
                                        className="form-select"
                                      >
                                        <option value="Active">{t("Active")}</option>
                                        <option value="Inactive">{t("Inactive")}</option>
                                        <option value="Under Approval">{t("Under Approval")}</option>
                                      </select>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            )}

                            {/* Address Tab */}
                            {activeTab === "address" && (
                              <Row className="g-4">
                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Address Line 1")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="address.line1"
                                      value={merchantData.address.line1}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors["address.line1"] ? "is-invalid" : ""
                                      }`}
                                    />
                                    {errors["address.line1"] && (
                                      <div className="invalid-feedback">
                                        {errors["address.line1"]}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Address Line 2")}
                                    </label>
                                    <input
                                      type="text"
                                      name="address.line2"
                                      value={merchantData.address.line2}
                                      onChange={handleInputChange}
                                      className="form-control"
                                    />
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("City")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="address.city"
                                      value={merchantData.address.city}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors["address.city"] ? "is-invalid" : ""
                                      }`}
                                    />
                                    {errors["address.city"] && (
                                      <div className="invalid-feedback">
                                        {errors["address.city"]}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("State/Province")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="address.state"
                                      value={merchantData.address.state}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors["address.state"] ? "is-invalid" : ""
                                      }`}
                                    />
                                    {errors["address.state"] && (
                                      <div className="invalid-feedback">
                                        {errors["address.state"]}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Postal Code")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="address.postcode"
                                      value={merchantData.address.postcode}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors["address.postcode"] ? "is-invalid" : ""
                                      }`}
                                    />
                                    {errors["address.postcode"] && (
                                      <div className="invalid-feedback">
                                        {errors["address.postcode"]}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Country")} *
                                    </label>
                                    <input
                                      type="text"
                                      name="address.country"
                                      value={merchantData.address.country}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      required
                                      className={`form-control ${
                                        errors["address.country"] ? "is-invalid" : ""
                                      }`}
                                    />
                                    {errors["address.country"] && (
                                      <div className="invalid-feedback">
                                        {errors["address.country"]}
                                      </div>
                                    )}
                                  </div>
                                </Col>

                                <Col md="12">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Google Map Location")}
                                    </label>
                                    <input
                                      type="text"
                                      name="address.googleMapLocation"
                                      value={merchantData.address.googleMapLocation}
                                      onChange={handleInputChange}
                                      placeholder={t("Google Maps URL or coordinates")}
                                      className="form-control"
                                    />
                                    <small className="form-text text-muted">
                                      {t("Paste Google Maps URL or coordinates (optional)")}
                                    </small>
                                  </div>
                                </Col>
                              </Row>
                            )}

                            {/* Documents Tab */}
                            {activeTab === "documents" && (
                              <Row className="g-4">
                                {merchantDocs && merchantDocs.length > 0 ? merchantDocs
                                  .filter(
                                    (doc) =>
                                      doc.status === "active" &&
                                      (doc.type === "both" ||
                                        (doc.type === "pharmacy" &&
                                          merchantData.businessCategory === "Pharmacy") ||
                                        (doc.type === "commerce" &&
                                          merchantData.businessCategory === "eCommerce"))
                                  )
                                  .map((doc) => (
                                    <Col md="6" key={doc._id}>
                                      <div className="form-group">
                                        <label className="form-label">
                                          {doc.title}
                                        </label>
                                        <input
                                          type="file"
                                          onChange={(e) =>
                                            handleFileChange(
                                              e,
                                              doc.title.toLowerCase().replace(/\s+/g, "")
                                            )
                                          }
                                          className="form-control"
                                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                        />
                                        {fileInfo[
                                          doc.title.toLowerCase().replace(/\s+/g, "")
                                        ] && (
                                          <div className="mt-2 d-flex align-items-center">
                                            <span className="text-muted me-2">
                                              {t("Current")}:{" "}
                                              {getDisplayFileName(
                                                fileInfo[
                                                  doc.title.toLowerCase().replace(/\s+/g, "")
                                                ]
                                              )}
                                            </span>
                                            <a
                                              href={`${FILE_BASE_URL}/getfiles/merchants/${
                                                fileInfo[
                                                  doc.title.toLowerCase().replace(/\s+/g, "")
                                                ].filename
                                              }`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="btn btn-sm btn-outline-primary"
                                            >
                                              {t("View")}
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  )) : (
                                    <Col md="12">
                                      <div className="text-center text-muted">
                                        {t("No documents available")}
                                      </div>
                                    </Col>
                                  )}

                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Other Documents
                    </label>
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, "otherDocs")}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    />
                    {fileInfo.otherDocs && fileInfo.otherDocs.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-1">
                          Current documents:
                        </p>
                        <ul className="list-disc pl-5">
                          {fileInfo.otherDocs.map((doc, index) => (
                            <li key={index} className="text-sm">
                              <span className="text-gray-600 mr-2">
                                {doc.filename}
                              </span>
                              <a
                                href={`${FILE_BASE_URL}/getfiles/merchants/${doc.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-teal-500 hover:text-teal-700"
                              >
                                View
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div> */}

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Store Logo")}
                                    </label>
                                    <input
                                      type="file"
                                      onChange={(e) => handleFileChange(e, "logo")}
                                      className="form-control"
                                      accept=".jpg,.jpeg,.png,.gif"
                                    />
                                    {fileInfo.logo && (
                                      <div className="mt-2 d-flex align-items-center">
                                        <span className="text-muted me-2">
                                          {t("Current")}: {getDisplayFileName(fileInfo.logo)}
                                        </span>
                                        <img
                                          src={getMerchantLogoUrl(fileInfo.logo)}
                                          alt={t("Store Logo")}
                                          className="rounded-circle me-2"
                                          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            )}

                            {/* Financial Tab */}
                            {activeTab === "financial" && (
                              <Row className="g-4">
                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Bank Name")}
                                    </label>
                                    <select
                                      name="bankInfo.bankName"
                                      value={merchantData.bankInfo.bankName}
                                      onChange={handleInputChange}
                                      className="form-select"
                                    >
                                      <option value="">{t("Select Bank")}</option>
                                      {banks && banks.length > 0 ? banks.map((bank) => (
                                        <option key={bank._id} value={bank.name}>
                                          {bank.name}
                                        </option>
                                      )) : (
                                        <option value="" disabled>{t("No banks available")}</option>
                                      )}
                                    </select>
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Account Number")}
                                    </label>
                                    <input
                                      type="text"
                                      name="bankInfo.accountNumber"
                                      value={merchantData.bankInfo.accountNumber}
                                      onChange={handleInputChange}
                                      className="form-control"
                                    />
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Account Holder Name")}
                                    </label>
                                    <input
                                      type="text"
                                      name="bankInfo.accountHolderName"
                                      value={merchantData.bankInfo.accountHolderName}
                                      onChange={handleInputChange}
                                      className="form-control"
                                    />
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Payment Terms")}
                                    </label>
                                    <select
                                      name="bankInfo.paymentTerms"
                                      value={merchantData.bankInfo.paymentTerms}
                                      onChange={handleInputChange}
                                      className="form-select"
                                    >
                                      <option value="Weekly">{t("Weekly")}</option>
                                      <option value="Bi-weekly">{t("Bi-weekly")}</option>
                                      <option value="Monthly">{t("Monthly")}</option>
                                    </select>
                                  </div>
                                </Col>

                                <Col md="6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Commission Rate")} (%)
                                    </label>
                                    <input
                                      type="number"
                                      name="commissionRate"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={merchantData.commissionRate}
                                      onChange={handleInputChange}
                                      onBlur={handleBlur}
                                      className={`form-control ${
                                        errors.commissionRate ? "is-invalid" : ""
                                      }`}
                                      placeholder={t("If no commission rate is set, default will be used")}
                                    />
                                    {errors.commissionRate && (
                                      <div className="invalid-feedback">
                                        {errors.commissionRate}
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            )}

                            {/* Settings Tab */}
                            {activeTab === "settings" && (
                              <Row className="g-4">
                                <Col md="12">
                                  <div className="form-group">
                                    <label className="form-label">
                                      {t("Store Description")}
                                    </label>
                                    <textarea
                                      name="storeDescription"
                                      value={merchantData.storeDescription}
                                      onChange={handleInputChange}
                                      rows="4"
                                      className="form-control"
                                      placeholder={t("Enter store description")}
                                    ></textarea>
                                  </div>
                                </Col>

                                <Col md="12">
                                  <div className="form-group">
                                    <label className="form-label mb-3">
                                      {t("Opening Hours")}
                                    </label>
                                    <div className="row g-3">
                                      {Object.keys(merchantData.openingHours).map((day) => (
                                        <div key={day} className="col-12">
                                          <div className="row align-items-center">
                                            <div className="col-md-3">
                                              <span className="form-label text-capitalize fw-medium">
                                                {t(day)}
                                              </span>
                                            </div>
                                            <div className="col-md-9">
                                              <div className="row g-2">
                                                <div className="col-md-6">
                                                  <div className="d-flex align-items-center">
                                                    <span className="text-muted me-2">
                                                      {t("Open")}:
                                                    </span>
                                                    <input
                                                      type="time"
                                                      value={merchantData.openingHours[day].open}
                                                      onChange={(e) =>
                                                        handleOpeningHoursChange(
                                                          day,
                                                          "open",
                                                          e.target.value
                                                        )
                                                      }
                                                      className="form-control form-control-sm"
                                                    />
                                                  </div>
                                                </div>
                                                <div className="col-md-6">
                                                  <div className="d-flex align-items-center">
                                                    <span className="text-muted me-2">
                                                      {t("Close")}:
                                                    </span>
                                                    <input
                                                      type="time"
                                                      value={merchantData.openingHours[day].close}
                                                      onChange={(e) =>
                                                        handleOpeningHoursChange(
                                                          day,
                                                          "close",
                                                          e.target.value
                                                        )
                                                      }
                                                      className="form-control form-control-sm"
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                            )}

                            <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                              <div className="d-flex gap-2">
                                {!isFirstTab() && (
                                  <button
                                    type="button"
                                    onClick={goToPreviousTab}
                                    className="btn btn-outline-secondary btn-hover-shine"
                                  >
                                    <i className="pe-7s-left me-1"></i>
                                    {t("Previous")}
                                  </button>
                                )}
                              </div>

                              <div className="d-flex gap-2">
                                {isEdit && isLastTab() && (
                                  <button
                                    type="button"
                                    onClick={handleSendCredentials}
                                    disabled={submitting}
                                    className="btn btn-info btn-hover-shine"
                                  >
                                    <i className="pe-7s-mail me-1"></i>
                                    {t("Send Credentials")}
                                  </button>
                                )}

                                {isLastTab() ? (
                                  <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn btn-custom-color btn-hover-shine"
                                  >
                                    <i className={`pe-7s-${submitting ? 'refresh' : isEdit ? 'check' : 'add-user'} me-1`}></i>
                                    {submitting
                                      ? t("Saving...")
                                      : isEdit
                                      ? t("Update Merchant")
                                      : t("Create Merchant")}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={handleNext}
                                    className="btn btn-custom-color btn-hover-shine"
                                  >
                                    <i className="pe-7s-right me-1"></i>
                                    {t("Next")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </form>
                        </CardBody>
                      </Card>
                    </Col>
                  </LoadingOverlay>
                </Row>
              </Col>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default MerchantForm;
